import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface BlobShapeProps {
  color: "purple" | "pink" | "cyan";
  position: { top?: string; bottom?: string; left?: string; right?: string };
  size?: string;
  delay?: number;
  isDark?: boolean;
}

export function BlobShape({ color, position, size = "400px", delay = 0, isDark = false }: BlobShapeProps) {
  const [blobSize, setBlobSize] = useState<string>(size);

  // Define gradients for light and dark modes
  const gradientMap: Record<string, { light: string; dark: string }> = {
    purple: {
      light: "from-purple-600/30 to-blue-600/30",
      dark: "from-purple-400/40 to-blue-400/40",
    },
    pink: {
      light: "from-pink-600/30 to-orange-600/30",
      dark: "from-pink-400/40 to-orange-400/40",
    },
    cyan: {
      light: "from-cyan-600/30 to-teal-600/30",
      dark: "from-cyan-400/40 to-teal-400/40",
    },
  };

  // Select the appropriate gradient based on isDark prop
  const gradientClass = isDark ? gradientMap[color].dark : gradientMap[color].light;

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      let responsiveSize = size;

      // Parse the size value (e.g., "400px" -> 400)
      const baseSize = parseInt(size, 10);

      // Adjust size based on viewport width
      if (width < 640) {
        // Mobile: 50% of original size
        responsiveSize = `${Math.round(baseSize * 0.5)}px`;
      } else if (width < 768) {
        // Small tablet: 65% of original size
        responsiveSize = `${Math.round(baseSize * 0.65)}px`;
      } else if (width < 1024) {
        // Tablet: 80% of original size
        responsiveSize = `${Math.round(baseSize * 0.8)}px`;
      } else {
        // Desktop: full size
        responsiveSize = size;
      }

      setBlobSize(responsiveSize);
    };

    // Set initial size
    handleResize();

    // Add event listener for window resize
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [size]);

  return (
    <motion.div
      className={`absolute rounded-full blur-3xl bg-gradient-to-br ${gradientClass} pointer-events-none`}
      style={{
        width: blobSize,
        height: blobSize,
        ...position,
      }}
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{
        scale: [0.8, 1.2, 0.8],
        opacity: [0.3, 0.6, 0.3],
        rotate: [0, 180, 360],
      }}
      transition={{
        duration: 20,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  );
}